import { v4 as uuid } from 'uuid';

interface DetailVm {
  name?: string;
  eventType?: string;
  eventDates?: string[];
  timeStart?: string;
  timeEnd?: string;
}

interface DescriptionVm {
  teaser?: string;
  description?: string;
  metadescription?: string;
  audience?: string;
}

interface OrganiserVm {
  firstname?: string;
  surname?: string;
  name?: string;
  email?: string;
  phone?: string;
}

interface VenueVm {
  name?: string;
  address?: string;
  googleplaceid?: string;
  latitude?: number;
  longitude?: number;
}

interface ImageVm {
  url?: string;
  title?: string;
  description?: string;
}

interface EventFormModel {
  detailVm: DetailVm;
  descriptionVm: DescriptionVm;
  organiserVm?: OrganiserVm;
  venueVm?: VenueVm;
  channelsVm?: { website?: string; phone?: string };
  bookingVm?: { bookingWebsite?: string; bookingPhone?: string };
  pricingVm?: { admission?: string; generalAdmissionPrice?: string };
  imagesVm?: { images?: ImageVm[] };
  videoVm?: { videoUrl?: string; videoCaption?: string; videoThumbnail?: string };
}

interface SchemaOrg {
  '@context': string;
  '@type': string[];
  [key: string]: unknown;
}

export class SchemaConverter {

  convertToSchema(model: EventFormModel): SchemaOrg {
    const thing: SchemaOrg = {
      '@context': 'http://schema.org',
      '@type': ['TouristAttraction'],
    };

    if (model.detailVm?.eventType) {
      const schemaType = model.detailVm.eventType === 'Market' ? 'Event' : model.detailVm.eventType;
      if (!thing['@type'].includes(schemaType)) {
        thing['@type'].push(schemaType);
      }
    }

    const additionalProps: Record<string, unknown>[] = [];
    if (model.detailVm?.eventType) {
      additionalProps.push({ '@type': 'PropertyValue', name: 'tags', value: [model.detailVm.eventType] });
    }
    if (model.descriptionVm?.metadescription) {
      additionalProps.push({ '@type': 'PropertyValue', name: 'metadescription', value: model.descriptionVm.metadescription });
    }
    if (additionalProps.length > 0) {
      thing.additionalProperty = additionalProps;
    }

    if (model.detailVm?.name)             thing.name = model.detailVm.name;
    if (model.descriptionVm?.teaser)      thing.disambiguatingDescription = model.descriptionVm.teaser;
    if (model.descriptionVm?.description) thing.description = model.descriptionVm.description;

    this.mapBookingAndPricing(model, thing);
    this.mapLocation(model, thing);
    this.mapOrganiser(model, thing);
    this.mapMedia(model, thing);
    this.mapEventDates(model, thing);

    if (model.descriptionVm?.audience) {
      thing.audience = { '@type': 'Audience', audienceType: model.descriptionVm.audience };
    }

    if (model.channelsVm?.phone)   thing.telephone = model.channelsVm.phone;
    if (model.channelsVm?.website) thing.url = model.channelsVm.website;

    const eventId = uuid();
    thing.id = eventId;
    thing.identifier = { '@type': 'PropertyValue', propertyID: 'EventsPortal', value: eventId };

    return thing;
  }

  private mapBookingAndPricing(model: EventFormModel, thing: SchemaOrg): void {
    thing.isAccessibleForFree = model.pricingVm?.admission === 'free';

    const offer: Record<string, unknown> = { '@type': 'Offer', price: 0 };

    if (model.bookingVm?.bookingWebsite || model.bookingVm?.bookingPhone) {
      offer.seller = {
        '@type': 'Organization',
        url: model.bookingVm.bookingWebsite,
        telephone: model.bookingVm.bookingPhone
      };
    }

    if (model.pricingVm?.generalAdmissionPrice) {
      offer.price = Number(model.pricingVm.generalAdmissionPrice);
      offer.priceCurrency = 'EUR';
    }

    thing.offers = offer;
  }

  private mapLocation(model: EventFormModel, thing: SchemaOrg): void {
    if (!model.venueVm) return;
    const v = model.venueVm;
    const place: Record<string, unknown> = { '@type': 'Place' };

    if (v.googleplaceid)                           place.identifier = v.googleplaceid;
    if (v.name)                                    place.name = v.name;
    if (v.latitude != null && v.longitude != null) place.geo = { '@type': 'GeoCoordinates', latitude: v.latitude, longitude: v.longitude };
    if (v.address)                                 place.address = v.address;

    thing.location = place;
  }

  private mapOrganiser(model: EventFormModel, thing: SchemaOrg): void {
    const o = model.organiserVm;
    if (!o) return;
    const name = (o.firstname ?? o.surname)
      ? [o.firstname, o.surname].filter((s): s is string => !!s).join(' ')
      : o.name;
    thing.organizer = { '@type': 'Person', telephone: o.phone, email: o.email, name };
  }

  private mapMedia(model: EventFormModel, thing: SchemaOrg): void {
    const images = model.imagesVm?.images;
    if (images && images.length > 0) {
      thing.image = images.map(img => ({
        '@type': 'ImageObject',
        url: img.url,
        name: img.title,
        description: img.description
      }));
    }

    if (model.videoVm?.videoUrl) {
      thing.subjectOf = {
        '@type': 'VideoObject',
        url: model.videoVm.videoUrl,
        caption: model.videoVm.videoCaption,
        description: model.videoVm.videoCaption,
        uploadDate: new Date().toISOString(),
        thumbnailUrl: model.videoVm.videoThumbnail,
        name: model.videoVm.videoCaption
      };
    }
  }

  private mapEventDates(model: EventFormModel, thing: SchemaOrg): void {
    const dates: string[] = model.detailVm?.eventDates ?? [];

    if (dates.length > 0) {
      const sorted = [...dates].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      thing.startDate = sorted[0];
      thing.endDate = sorted[sorted.length - 1];

      if (sorted.length > 1) {
        thing.eventSchedule = sorted.map(date => ({
          startDate: date,
          endDate: date,
          repeatFrequency: 'P1D',
          '@type': 'Schedule'
        }));
      }
    }

    if (model.detailVm?.timeStart) {
      thing.doorTime = model.detailVm.timeStart;
    }

    const timeStart = model.detailVm?.timeStart;
    const timeEnd = model.detailVm?.timeEnd;
    if (timeStart && timeEnd) {
      const startParts = timeStart.split(':');
      const endParts = timeEnd.split(':');
      const durationMins = (Number(endParts[0]) * 60 + Number(endParts[1])) - (Number(startParts[0]) * 60 + Number(startParts[1]));
      if (durationMins > 0) {
        const hours = Math.floor(durationMins / 60);
        const mins = durationMins % 60;
        thing.duration = `T${hours}H${mins}M0S`;
      }
    }
  }
}
